import { isNil } from 'lodash';

import {
  StageStatusEnum,
  BOUserStatusEnum,
  CustomerStatusEnum,
  HistoryResponseModel,
  FullBOUserResponseModel,
  FullCustomerInfoResponseModel,
  AssignmentHistoryResponse,
} from 'api/adminApiClient';
import {
  isStageCompleted,
  isMarkedAsStopStage,
} from 'utils/profileProgressUtils/generalProgressUtils';
import { getFullName } from 'utils/personUtils';
import { ActivitiesSectionPageTypeEnum } from '../enums';
import { getMemberProgressTrackerStageName } from 'utils/profileProgressUtils/memberProgressUtils';
import { getCustomerProgressTrackerStageName } from 'utils/profileProgressUtils/customerProgressUtils';
import { getAssignmentProgressTrackerStageName } from 'utils/profileProgressUtils/assignmentProgressUtils';

// Progress tracker logs utils
const getStageActionName = (stageStatus?: StageStatusEnum) => {
  switch (stageStatus) {
    case StageStatusEnum.SoftStop:
      return 'sent soft stop for';
    case StageStatusEnum.HardStop:
      return 'sent hard stop for';
    case StageStatusEnum.SoftStopHalfCompleted:
      return 'half completed';
    case StageStatusEnum.Completed:
      return 'completed';
    default:
      return '';
  }
};

const getMemberStageHistoryLogDescription = (
  historyData: FullBOUserResponseModel,
) => {
  if (isNil(historyData?.status) || isNil(historyData?.stageStatus)) {
    return '';
  }

  const stageName = getMemberProgressTrackerStageName(historyData.status, true);
  const actionName = getStageActionName(historyData.stageStatus);
  const stageDescription = `${actionName} ${stageName} stage`;

  if (isMarkedAsStopStage(historyData.stageStatus)) {
    return stageDescription;
  }

  if (isStageCompleted(historyData.stageStatus)) {
    switch (historyData.status) {
      case BOUserStatusEnum.PriceAgreementSigned:
        return 'signed Pricing and service agreement';

      case BOUserStatusEnum.Completed:
        return 'profile has been approved';

      default:
        return stageDescription;
    }
  }

  return '';
};

const getCustomerStageHistoryLogDescription = (
  historyData: FullCustomerInfoResponseModel,
) => {
  if (isNil(historyData?.status) || isNil(historyData?.stageStatus)) {
    return '';
  }

  const stageName = getCustomerProgressTrackerStageName(historyData.status);
  const actionName = getStageActionName(historyData.stageStatus);
  const stageDescription = `${actionName} ${stageName} stage`;

  if (isMarkedAsStopStage(historyData.stageStatus)) {
    return stageDescription;
  }

  if (isStageCompleted(historyData.stageStatus)) {
    switch (historyData.status) {
      case CustomerStatusEnum.NewCustomerSetupComplete:
        return `approved ${
          historyData.generalInfo?.generalInfo?.companyInfo?.companyName || ''
        } customer`;

      default:
        return stageDescription;
    }
  }

  return '';
};

const getAssignmentStageHistoryLogDescription = (
  historyData: AssignmentHistoryResponse,
) => {
  if (isNil(historyData?.status) || isNil(historyData?.stageStatus)) {
    return '';
  }

  const stageName = getAssignmentProgressTrackerStageName(historyData.status);
  const actionName = getStageActionName(historyData.stageStatus);
  const stageDescription = `${actionName} ${stageName} stage`;

  if (
    isMarkedAsStopStage(historyData.stageStatus) ||
    isStageCompleted(historyData.stageStatus)
  ) {
    return stageDescription;
  }

  return '';
};

export const getStageHistoryLogDescription = (
  historyItem: HistoryResponseModel,
  pageType: ActivitiesSectionPageTypeEnum,
) => {
  const { newData } = historyItem.details;

  if (isNil(newData)) {
    return '';
  }

  let stageDescription = '';

  switch (pageType) {
    case ActivitiesSectionPageTypeEnum.Customer:
      stageDescription = getCustomerStageHistoryLogDescription(newData);
      break;

    case ActivitiesSectionPageTypeEnum.Member:
      stageDescription = getMemberStageHistoryLogDescription(newData);
      break;

    case ActivitiesSectionPageTypeEnum.Assignment:
      stageDescription = getAssignmentStageHistoryLogDescription(newData);
      break;
  }

  if (!stageDescription) return '';

  const fullName = getFullName(historyItem.firstName, historyItem.lastName);

  return `${fullName} ${stageDescription}`;
};

export const getYesNoAnswer = (answer?: boolean) => {
  if (isNil(answer)) return undefined;

  return answer ? 'Yes' : 'No';
};
